import { Time } from "./TimeHelper";
import { getLanguage } from "@shared/localization/Localization";

export namespace Description {
    export class Number {
        static decimal(
            value: number | undefined,
            options: Intl.NumberFormatOptions = {},
            language: string = getLanguage()
        ): string {
            return (value ?? 0).toLocaleString(language, options);
        }
    }

    export class Timestamp {
        /**
         * How far in the future a timestamp is ("en 2 días"). The mirror of `timeAgo`,
         * which only ever reads backwards — handing it a future timestamp produces a
         * negative count and phrasing like "-172785 segundos atrás".
         */
        static timeUntil(timestamp: number): string {
            const now = Date.now();

            // Guard on the raw milliseconds, not on the floored seconds: flooring a
            // sub-second gap rounds toward negative on one side and to zero on the
            // other, so second-based guards let the two helpers delegate to each other
            // forever for a timestamp a few hundred milliseconds away.
            if (timestamp <= now) {
                return this.timeAgo(timestamp);
            }

            const secondsAhead = Math.floor((timestamp - now) / 1000);

            if (secondsAhead < 60) {
                return 'en menos de un minuto'.localized();
            }

            const minutesAhead = Math.floor(secondsAhead / 60);
            if (minutesAhead < 60) {
                return minutesAhead === 1 ? 'en 1 minuto'.localized() : `en `.localized() + `${minutesAhead}` + ` minutos`.localized();
            }

            const hoursAhead = Math.floor(minutesAhead / 60);
            if (hoursAhead < 24) {
                return hoursAhead === 1 ? 'en 1 hora'.localized() : `en `.localized() + `${hoursAhead}` + ` horas`.localized();
            }

            const daysAhead = Math.floor(hoursAhead / 24);
            if (daysAhead < 30) {
                return daysAhead === 1 ? 'en 1 día'.localized() : `en `.localized() + `${daysAhead}` + ` días`.localized();
            }

            const monthsAhead = Math.floor(daysAhead / 30);
            if (monthsAhead < 12) {
                return monthsAhead === 1 ? 'en 1 mes'.localized() : `en `.localized() + `${monthsAhead}` + ` meses`.localized();
            }

            const yearsAhead = Math.floor(monthsAhead / 12);
            return yearsAhead === 1 ? 'en 1 año'.localized() : `en `.localized() + `${yearsAhead}` + ` años`.localized();
        }

        static timeAgo(timestamp: number): string {
            const now = Date.now();

            // A future timestamp would otherwise fall through every branch below as a
            // negative count and render as "-172785 segundos atrás". Compared in raw
            // milliseconds so this and `timeUntil` can never both delegate.
            if (timestamp > now) {
                return this.timeUntil(timestamp);
            }

            const secondsPast = Math.floor((now - timestamp) / 1000);

            if (secondsPast < 60) {
                return secondsPast === 1 ? '1 segundo atrás'.localized() : `${secondsPast}` + ` segundos atrás`.localized();
            }
        
            const minutesPast = Math.floor(secondsPast / 60);
            if (minutesPast < 60) {
                return minutesPast === 1 ? 'hace 1 minuto'.localized() : `${minutesPast}` + ` minutos atrás`.localized();
            }
        
            const hoursPast = Math.floor(minutesPast / 60);
            if (hoursPast < 24) {
                return hoursPast === 1 ? 'hace 1 hora'.localized() : `${hoursPast}` + ` horas atrás`.localized();
            }
        
            const daysPast = Math.floor(hoursPast / 24);
            if (daysPast < 30) {
                return daysPast === 1 ? '1 día atrás'.localized() : `${daysPast}` + ` días atrás`.localized();
            }
        
            const monthsPast = Math.floor(daysPast / 30);
            if (monthsPast < 12) {
                return monthsPast === 1 ? '1 mes atrás'.localized() : `${monthsPast}` + ` meses atrás`.localized();
            }
        
            const yearsPast = Math.floor(monthsPast / 12);
            return yearsPast === 1 ? '1 año atrás'.localized() : `${yearsPast}` + ` años atrás`.localized();
        }

        static timeAgoWithDate(timestamp: number, timezone?: string): string {
            const now = Date.now();
            if (now - timestamp <= Time.hoursInMiliseconds(2)) {
                return this.timeAgo(timestamp);
            }

            // Format the date considering the timezone
            const date = new Date(timestamp);
            const options: Intl.DateTimeFormatOptions = {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: timezone,
            };

            return date.toLocaleString('es-ES', options); 
        }

        static timeAgoWithShortDate(timestamp: number, timezone?: string): string {
            const now = Date.now();
            if (now - timestamp <= Time.hoursInMiliseconds(2)) {
                return this.timeAgo(timestamp);
            }

            // Format the date considering the timezone
            const date = new Date(timestamp);
            const options: Intl.DateTimeFormatOptions = {
                year: 'numeric',
                month: '2-digit',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: timezone,
            };

            return date.toLocaleString('es-ES', options); 
        }

        static date(timestamp: number, timezone: string): string {
            // Format the date considering the timezone
            const date = new Date(timestamp);
            const options: Intl.DateTimeFormatOptions = {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: timezone,
            };

            return date.toLocaleString('es-ES', options); 
        }
    }
}
